import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { Select } from "@/components/select";
import { ArrowLeftIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { useQueryGetExamProgress } from "@/apis/exam_progress/getExamProgress";
import { getExamPaperReview } from "@/apis/exam_progress/getExamPaperReview";
import { useMutationUpdateExamProgressAnswers } from "@/apis/exam_progress/updateExamProgressAnswers";
import { Input } from "@/components/input";
import { Radio, RadioGroup, RadioField } from "@/components/radio";
import { Fieldset, Legend, Label } from "@/components/fieldset";
import { Dialog } from "@/components/dialog";
import type {
  IExamProgressAnswer,
  IAssignedExamPaper,
  SubjectType,
} from "@/apis/exam_progress/types";
import type { IQuestion } from "@/apis/exam_papers/types";
import { getPaperTypeLabel } from "@/constants/exam";
import { message } from "antd";

interface IAnswerState {
  [questionId: string]: {
    selected_answer?: string;
    excluded_choices?: string[];
    marked_for_review?: boolean;
  };
}

const ExamProgressEditAnswersPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // 로컬 답안 상태
  const [answers, setAnswers] = useState<IAnswerState>({});

  // 선택된 과목과 섹션 상태
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string>("");

  // 저장 mutation
  const { mutate: updateAnswers, isPending: isSaving } =
    useMutationUpdateExamProgressAnswers();

  // API 호출
  const {
    data: examProgressData,
    isLoading: isProgressLoading,
    error: progressError,
  } = useQueryGetExamProgress(id || "");

  // 각 과목별 문제 데이터를 가져오는 쿼리들 - useQueries 사용
  const paperReviewQueries = useQueries({
    queries: (examProgressData?.assigned_papers || []).map((paper) => ({
      queryKey: ["exam_paper_review", id, paper.exam_paper_id],
      queryFn: () => getExamPaperReview(id || "", paper.exam_paper_id),
      enabled: !!id && !!paper.exam_paper_id,
    })),
  });

  // 초기 답안 상태 설정
  useEffect(() => {
    if (examProgressData?.answers) {
      const initialAnswers: IAnswerState = {};
      examProgressData.answers.forEach((answer) => {
        initialAnswers[answer.question_id] = {
          selected_answer: answer.selected_answer,
          excluded_choices: answer.excluded_choices,
          marked_for_review: answer.marked_for_review,
        };
      });
      setAnswers(initialAnswers);
    }
  }, [examProgressData]);

  // 첫 번째 과목과 섹션을 기본값으로 설정
  useEffect(() => {
    if (
      examProgressData?.assigned_papers &&
      examProgressData.assigned_papers.length > 0
    ) {
      if (!selectedSubject) {
        setSelectedSubject(examProgressData.assigned_papers[0].subject);
      }

      // 첫 번째 쿼리 결과가 있으면 첫 번째 섹션을 선택
      const firstQuery = paperReviewQueries[0];
      if (
        firstQuery?.data?.sections &&
        firstQuery.data.sections.length > 0 &&
        !selectedSection
      ) {
        setSelectedSection(firstQuery.data.sections[0].section_no.toString());
      }
    }
  }, [examProgressData, paperReviewQueries, selectedSubject, selectedSection]);

  // 답안 변경 핸들러
  const handleAnswerChange = useCallback(
    (questionId: string, selectedAnswer: string) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          selected_answer: selectedAnswer,
        },
      }));
    },
    []
  );

  // 과목 옵션 생성
  const subjectOptions = useMemo(() => {
    if (!examProgressData?.assigned_papers) return [];

    return examProgressData.assigned_papers.map((paper) => ({
      value: paper.subject,
      label: getPaperTypeLabel(paper.subject),
    }));
  }, [examProgressData]);

  // 현재 선택된 과목의 exam_type 가져오기
  const currentExamType = useMemo(() => {
    if (!examProgressData?.assigned_papers || !selectedSubject) return null;

    const paperIndex = examProgressData.assigned_papers.findIndex(
      (paper) => paper.subject === selectedSubject
    );
    if (paperIndex === -1) return null;

    const query = paperReviewQueries[paperIndex];
    return query?.data?.paper?.exam_type || null;
  }, [examProgressData, selectedSubject, paperReviewQueries]);

  // Section 또는 Module 텍스트 결정
  const sectionLabel = currentExamType === "SAT" ? "Module" : "Section";

  // 섹션 옵션 생성 (선택된 과목에 따라)
  const sectionOptions = useMemo(() => {
    if (!examProgressData?.assigned_papers || !selectedSubject) return [];

    const paperIndex = examProgressData.assigned_papers.findIndex(
      (paper) => paper.subject === selectedSubject
    );
    if (paperIndex === -1) return [];

    const query = paperReviewQueries[paperIndex];
    if (!query?.data?.sections) return [];

    return query.data.sections.map((section: any) => ({
      value: section.section_no.toString(),
      label: `${sectionLabel} ${section.section_no}`,
    }));
  }, [examProgressData, selectedSubject, paperReviewQueries, sectionLabel]);

  // 현재 선택된 섹션 데이터 가져오기
  const currentSectionData = useMemo(() => {
    if (
      !examProgressData?.assigned_papers ||
      !selectedSubject ||
      !selectedSection
    )
      return null;

    const paperIndex = examProgressData.assigned_papers.findIndex(
      (paper) => paper.subject === selectedSubject
    );
    if (paperIndex === -1) return null;

    const query = paperReviewQueries[paperIndex];
    if (!query?.data?.sections) return null;

    return query.data.sections.find(
      (section: any) => section.section_no.toString() === selectedSection
    );
  }, [examProgressData, selectedSubject, selectedSection, paperReviewQueries]);

  // 현재 선택된 exam_paper_id 가져오기
  const currentExamPaperId = useMemo(() => {
    if (!examProgressData?.assigned_papers || !selectedSubject) return null;

    const paper = examProgressData.assigned_papers.find(
      (paper) => paper.subject === selectedSubject
    );
    return paper?.exam_paper_id || null;
  }, [examProgressData, selectedSubject]);

  // 저장 핸들러
  const handleSave = useCallback(() => {
    if (!examProgressData?.id || !currentExamPaperId || !currentSectionData) {
      console.error("Missing required data for saving");
      return;
    }

    // 현재 섹션의 문제들에 대한 답안만 수집
    const answersToSave = currentSectionData.questions
      .map((question: any) => {
        const answerValue = answers[question.id]?.selected_answer;
        if (answerValue) {
          return {
            question_id: question.id,
            selected_answer: answerValue,
          };
        }
        return null;
      })
      .filter(
        (answer): answer is { question_id: string; selected_answer: string } =>
          answer !== null
      );

    if (answersToSave.length === 0) {
      console.warn("No answers to save");
      return;
    }

    updateAnswers(
      {
        progressId: examProgressData.id,
        examPaperId: currentExamPaperId,
        answers: answersToSave,
      },
      {
        onSuccess: () => {
          console.log("Answers saved successfully");

          // 관련 쿼리들을 invalidate하여 최신 데이터로 새로고침
          queryClient.invalidateQueries({
            queryKey: ["exam_progress", id],
          });
          queryClient.invalidateQueries({
            queryKey: ["exam_paper_review", id, currentExamPaperId],
          });

          // 성공 메시지 표시
          message.success("Answers saved successfully!");
        },
        onError: (error: any) => {
          console.error("Failed to save answers:", error);
          // 에러 메시지 표시
          message.error(
            error?.message || "Failed to save answers. Please try again."
          );
        },
      }
    );
  }, [
    examProgressData,
    currentExamPaperId,
    currentSectionData,
    answers,
    updateAnswers,
  ]);

  // 문제 렌더링 컴포넌트
  const QuestionItem: React.FC<{
    question: any;
    questionIndex: number;
    answer: any;
  }> = ({ question, questionIndex, answer }) => {
    const currentAnswer = answers[question.id]?.selected_answer || "";

    return (
      <div className="border border-gray-200 rounded-lg p-4 mb-4">
        <div className="mb-3">
          <Text className="font-semibold text-sm text-gray-600">
            Question {questionIndex + 1}
          </Text>
          <div
            className="quill-content mt-1 whitespace-pre-wrap text-[18px] text-gray-900 leading-relaxed font-serif font-times [&_img]:block [&_img]:mx-auto"
            style={{ fontFamily: '"Times New Roman", Times, serif' }}
            dangerouslySetInnerHTML={{ __html: question.question }}
          />
        </div>

        {question.answer_type === "multiple_choice" ? (
          // 객관식 - QuestionPanel 스타일 적용
          <div className="flex flex-col gap-3 w-full">
            {question.choices?.map((choice: any, index: number) => {
              const choiceLetter = String.fromCharCode(65 + index);
              const isSelected = currentAnswer === choice.id;

              let labelClass =
                "flex items-center px-0 py-0 cursor-pointer transition-all font-sans rounded-lg border pl-4 flex-1 min-h-[56px] relative group ";

              if (isSelected) {
                labelClass +=
                  "border-blue-700 bg-[#f5f8ff] shadow-[0_0_0_2px_#2563eb] ";
              } else {
                labelClass += "border-[#d1d5db] bg-white hover:bg-[#f3f4f6] ";
              }

              return (
                <div key={choice.id} className="flex items-stretch w-full">
                  <label
                    className={labelClass}
                    style={{ padding: 0 }}
                    onClick={() => handleAnswerChange(question.id, choice.id)}
                  >
                    {/* Choice letter circle - QuestionPanel 스타일 */}
                    <span
                      className={`flex items-center justify-center w-6 h-6 rounded-full mr-4 ml-4 mt-0.5 text-base font-semibold text-[14px] transition-all border-1 ${
                        isSelected
                          ? "bg-blue-700 text-white border-black"
                          : "bg-white text-black border-[#bfc8e6]"
                      }`}
                      style={{ minWidth: 28, minHeight: 28 }}
                    >
                      {choiceLetter}
                    </span>

                    {/* Choice text */}
                    <div className="flex-1 min-w-0 flex items-center h-full w-full">
                      <div
                        className="quill-content flex-1 font-normal w-full h-full text-[18px] text-gray-900 leading-relaxed font-serif font-times flex items-center"
                        style={{
                          fontFamily: '"Times New Roman", Times, serif',
                        }}
                        dangerouslySetInnerHTML={{ __html: choice.text }}
                      />
                    </div>

                    {/* 정답 표시 - choice 우측 하단 */}
                    {answer?.correct_answer === choice.id && (
                      <div className="absolute bottom-1 right-2 text-xs text-green-600 font-medium">
                        ✓ Correct
                      </div>
                    )}

                    {/* 학생의 원래 답안 표시 (선생님이 수정할 때만) - 우측 상단 */}
                    {answer?.selected_answer === choice.id &&
                      answer?.selected_answer !== currentAnswer && (
                        <div className="absolute top-1 right-2 text-xs text-blue-600 font-medium">
                          Student's answer
                        </div>
                      )}
                  </label>
                </div>
              );
            })}
          </div>
        ) : (
          // 단답형
          <Input
            value={currentAnswer}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Enter your answer"
            className="w-full"
          />
        )}
      </div>
    );
  };

  if (isProgressLoading) {
    return (
      <PageContainer>
        <Text>Loading exam progress...</Text>
      </PageContainer>
    );
  }

  if (progressError) {
    return (
      <PageContainer>
        <Text>Error loading exam progress data</Text>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button plain onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>

          <div>
            <Heading>Edit Student Answers</Heading>
            <Text className="text-zinc-500">
              Review and modify student's exam answers
            </Text>
          </div>
        </div>

        {/* Save Changes 버튼을 상단으로 이동 */}
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* 과목과 섹션 선택 */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <Select
            value={selectedSubject}
            onChange={(e) => {
              setSelectedSubject(e.target.value);
              setSelectedSection(""); // 과목 변경 시 섹션 초기화
            }}
          >
            <option value="">Select a subject</option>
            {subjectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {sectionLabel}
          </label>
          <Select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            disabled={!selectedSubject}
          >
            <option value="">{`Select a ${sectionLabel.toLowerCase()}`}</option>
            {sectionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* 선택된 섹션의 문제들을 1열로 표시 */}
      {currentSectionData ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <Heading level={3} className="mb-4">
            {getPaperTypeLabel(selectedSubject)} - {sectionLabel} {selectedSection}
          </Heading>

          <div className="space-y-4">
            {currentSectionData.questions?.map(
              (question: any, questionIndex: number) => {
                const answer = currentSectionData.answers?.find(
                  (a: any) => a.question_id === question.id
                );
                return (
                  <QuestionItem
                    key={question.id}
                    question={question}
                    questionIndex={questionIndex}
                    answer={answer}
                  />
                );
              }
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <Text className="text-gray-500">
            {!selectedSubject || !selectedSection
              ? `Please select a subject and ${sectionLabel.toLowerCase()} to view questions`
              : "Loading questions..."}
          </Text>
        </div>
      )}
    </PageContainer>
  );
};

export default ExamProgressEditAnswersPage;
